import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

const PrivateRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [redirectTo, setRedirectTo] = useState("/login");
  const location = useLocation();

  useEffect(() => {
    // Check if embedded in Shopify Admin
    const urlParams = new URLSearchParams(window.location.search);
    const shopFromUrl = urlParams.get('shop');
    const hostFromUrl = urlParams.get('host');
    const isEmbedded = window.top !== window.self || hostFromUrl;

    // If embedded with shop param - auto-authenticate
    if (isEmbedded && shopFromUrl) {
      console.log('🛍️ PrivateRoute: Embedded mode with shop:', shopFromUrl);
      localStorage.setItem('shop_domain', shopFromUrl);
      localStorage.setItem('shopDomain', shopFromUrl);
      localStorage.setItem('onboarding_completed', 'true');
      setIsValid(true);
      setIsChecking(false);
      return;
    }

    // If embedded without shop param - allow if has any shop
    if (isEmbedded) {
      const existingShop = localStorage.getItem('shop_domain') || localStorage.getItem('shopDomain');
      if (existingShop && existingShop !== 'demo-shop.myshopify.com') {
        console.log('🛍️ PrivateRoute: Embedded mode, using existing shop:', existingShop);
        setIsValid(true);
        setIsChecking(false);
        return;
      }
    }

    // Standard authentication checks for standalone mode
    const token = localStorage.getItem("token");
    const shopDomain = localStorage.getItem("shop_domain") || localStorage.getItem("shopDomain");
    const shopId = localStorage.getItem("shop_id") || localStorage.getItem("shopId");

    const hasToken = Boolean(token && token !== "undefined" && token !== "null");
    const hasShop = Boolean(shopDomain && shopDomain !== "undefined" && shopDomain !== "null" && shopDomain !== "");
    const hasShopId = Boolean(shopId && shopId !== "undefined" && shopId !== "null");

    // Demo Mode
    const isDemoShop = shopDomain === "demo-shop.myshopify.com" || localStorage.getItem("isDemo") === "true";

    console.log("🔐 PrivateRoute check:", {
      path: location.pathname,
      shopDomain,
      hasToken,
      hasShop,
      hasShopId,
      isDemoShop,
      isEmbedded
    });

    if (isDemoShop) {
      setIsValid(true);
      setIsChecking(false);
      return;
    }

    if (!hasShop) {
      setRedirectTo("/login");
      setIsValid(false);
      setIsChecking(false);
      return;
    }

    // Relaxed check - only need shop and token OR shop alone for embedded
    if (!hasToken && !isEmbedded) {
      setRedirectTo("/onboarding");
      setIsValid(false);
      setIsChecking(false);
      return;
    }

    // All good
    setIsValid(true);
    setIsChecking(false);
  }, [location.pathname]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-2 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    const from = location.pathname;
    localStorage.setItem("redirectAfterLogin", from);
    console.log(`🔄 Redirecting to ${redirectTo} from ${from}`);
    return <Navigate to={redirectTo} replace state={{ from }} />;
  }

  return children;
};

export default PrivateRoute;